import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { RadioGroup, RadioGroupItem } from "./ui/radio-group"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface AnalisisQuimicoFormData {
  // Información básica
  numeroSolicitud: string
  fecha: string

  // Norma seleccionada (solo una puede estar activa)
  normaSeleccionada: string

  // Personal
  realizo: string
  superviso: string

  // Resultados y análisis
  resultadoQuimico: string
  observaciones: string
  mostrarObservaciones: boolean
  pretratamiento: string

  // Apresto
  p0: string
  pf: string
  fibra: string

  // Contenido de fibras (NMX-1833)
  m0: string
  m1: string
  d: string
  fibra_ro: string
  fibra_cs: string

  // Contenido de fibras (NMX-084)
  combustion: string
  microscopia: string
  analisis_quimico: string

  // Sustancia (con pretratamiento)
  sustancia: string
}

interface AnalisisQuimicoFormProps {
  onBack: () => void
  pedidoId: string
  pedidoRowId?: string
  mode: 'create' | 'view' | 'edit'
  initialData?: Partial<AnalisisQuimicoFormData>
  realizoUser?: string
  onSave?: (data: AnalisisQuimicoFormData) => void
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function AnalisisQuimicoForm({ onBack, pedidoId, pedidoRowId, mode, initialData, realizoUser, onSave, onRefreshBackend, onAutosave }: AnalisisQuimicoFormProps & { onRefreshBackend?: () => void }) {
  // Soporta array y objeto para initialData
  const initialDataObj = Array.isArray(initialData) ? initialData[0] : initialData || {};
  const globalRol = useUserRol ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = globalRol;
  // Lógica robusta para aceptado/rechazado
  const aceptadoRaw = (initialDataObj as any)?.ACEPTADO ?? (initialDataObj as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';

  // --- Agregar subnorma si se selecciona NMX-A-1833/1-INNTEX-2014 ---
  // Al inicializar, si initialDataObj.OPCION_NMX existe, úsalo para normaSeleccionada
  const opcionNmxRaw = (initialDataObj as any)?.OPCION_NMX || initialDataObj.normaSeleccionada || '';
  let defaultNorma = '';
  let defaultSubnorma = '';
  if (opcionNmxRaw.includes(',')) {
    const [main, sub] = opcionNmxRaw.split(',').map((s: string) => s.trim());
    defaultNorma = main === 'NMX-A-1833/1-INNTEX-2014' ? 'norma1833' : (main === 'NMX-A-084-INNTEX-2015' ? 'norma084' : main);
    defaultSubnorma = sub || '';
  } else {
    defaultNorma = opcionNmxRaw === 'NMX-A-1833/1-INNTEX-2014' ? 'norma1833' : (opcionNmxRaw === 'NMX-A-084-INNTEX-2015' ? 'norma084' : opcionNmxRaw);
  }

  console.log('[AnalisisQuimicoForm] initialDataObj:', initialDataObj);
  console.log('[AnalisisQuimicoForm] OPCION_NMX:', (initialDataObj as any)?.OPCION_NMX);
  console.log('[AnalisisQuimicoForm] normaSeleccionada:', defaultNorma, 'subnorma:', defaultSubnorma);

  const [formData, setFormData] = useState<AnalisisQuimicoFormData & { PRUEBA?: string; ROW_ID?: string }>({
    ...initialDataObj,
    numeroSolicitud: initialDataObj.numeroSolicitud || "",
    fecha: initialDataObj.fecha || "",
    normaSeleccionada: defaultNorma,
    realizo: (mode === 'create' && realizoUser) ? realizoUser : initialDataObj.realizo || "",
    superviso: initialDataObj.superviso || "",
    resultadoQuimico: initialDataObj.resultadoQuimico || "",
    observaciones: initialDataObj.observaciones || "",
    mostrarObservaciones: (initialDataObj as any)?.MOSTRAR_OBSERVACIONES === true,
    pretratamiento: initialDataObj.pretratamiento || "",
    p0: (initialDataObj as any)?.p0 || "",
    pf: (initialDataObj as any)?.pf || "",
    fibra: (initialDataObj as any)?.fibra || "",
    m0: (initialDataObj as any)?.m0 || "",
    m1: (initialDataObj as any)?.m1 || "",
    d: (initialDataObj as any)?.d || "",
    fibra_ro: (initialDataObj as any)?.fibra_ro || "",
    fibra_cs: (initialDataObj as any)?.fibra_cs || "",
    combustion: (initialDataObj as any)?.combustion || "",
    microscopia: (initialDataObj as any)?.microscopia || "",
    analisis_quimico: (initialDataObj as any)?.analisis_quimico || "",
    sustancia: (initialDataObj as any)?.sustancia || "",
    PRUEBA: (initialDataObj as any)?.PRUEBA || '',
    ROW_ID: (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '',
  });

  // --- Agregar subnorma si se selecciona NMX-A-1833/1-INNTEX-2014 ---
  const [subnorma1833, setSubnorma1833] = useState<string>(defaultSubnorma);

  // --- Reportar apresto cuando hay pretratamiento ---
  const [reportarApresto, setReportarApresto] = useState<string>((initialDataObj as any)?.reportarApresto || '');

  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>((initialDataObj as any)?.ACEPTAR || '');
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const handleInputChange = (field: keyof AnalisisQuimicoFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Si cambia la norma principal, limpiar subnorma
    if (field === "normaSeleccionada" && value !== "norma1833") {
      setSubnorma1833("");
    }
    // Si cambia a sin_pretratamiento, limpiar reportarApresto
    if (field === "pretratamiento" && value === "sin_pretratamiento") {
      setReportarApresto("");
    }
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      const dataToSave = processNormaData();
      if (onSave) {
        onSave(dataToSave);
      }
    } finally {
      setGuardando(false)
    }
  }

  // Corregir lógica: solo considerar isAceptado en modo 'view', nunca en modo 'create'
  const isReadOnly = mode === 'view' && !editMode

  // --- FUNCIÓN HELPER PARA PROCESAR NORMA ---
  const processNormaData = () => {
    let normaFinal = formData.normaSeleccionada;
    if (formData.normaSeleccionada === 'norma1833') {
      normaFinal = 'NMX-A-1833/1-INNTEX-2014';
      if (subnorma1833) {
        normaFinal += `, ${subnorma1833}`;
      }
    } else if (formData.normaSeleccionada === 'norma084') {
      normaFinal = 'NMX-A-084-INNTEX-2015';
    }
    return {
      ...formData,
      normaSeleccionada: normaFinal,
      OPCION_NMX: normaFinal,
      reportarApresto,
    };
  };

  // --- ADMIN ACTIONS ---
  // RowId robusto: primero formData, luego initialDataObj, luego vacío
  const [autosaveRowId, setAutosaveRowId] = useState<string>((formData as any)?.ROW_ID || (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialDataObj as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = (formData as any).PEDIDO || pedidoId || '';
  const pruebaValue = (formData as any).PRUEBA || '';

  const formPayload = () => {
    const { ROW_ID, PRUEBA, PEDIDO, nomId: _nomId, ...rest } = processNormaData() as any;
    return rest;
  };

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'ANÁLISIS QUÍMICO CUALITATIVO Y CUANTITATIVO',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, subnorma1833, reportarApresto, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (
    accion: 'aceptado' | 'rechazado',
    razonRechazo?: string
  ) => {
    if (accion === 'rechazado' && !razonRechazo) {
      setShowRechazoModal(true);
      return;
    }
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba Analisis Quimico?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba Analisis Quimico?';
    if (!window.confirm(confirmMsg)) return;
    setRechazoLoading(true);
    const processedData = processNormaData();
    const payload = {
      ...processedData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' && razonRechazo ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      setEstatus(payload.ACEPTAR);
      setEditMode(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
      setShowRechazoModal(false);
    }
  };

  // --- APRESTO ---
  const showApresto = formData.pretratamiento === 'con_pretratamiento'

  const p0Num = parseFloat(formData.p0 || '0')
  const pfNum = parseFloat(formData.pf || '0')
  const pctA = p0Num > 0 ? (((p0Num - pfNum) / p0Num) * 100).toFixed(1) : ''

  // Contenido de fibras
  const m0Num = parseFloat(formData.m0 || '0')
  const m1Num = parseFloat(formData.m1 || '0')
  const dNum  = parseFloat(formData.d  || '0')
  const roVal = m0Num > 0 ? ((100 * m1Num * dNum) / m0Num).toFixed(1) : ''
  const csVal = roVal !== '' ? (100 - parseFloat(roVal)).toFixed(1) : ''

  // Auto-generar resultado para NMX-1833 en modo create/edit
  useEffect(() => {
    if (formData.normaSeleccionada !== 'norma1833') return;
    if (mode === 'view' && !editMode) return;

    const parts: string[] = [];
    if (roVal !== '' && formData.fibra_ro) parts.push(`${roVal}% ${formData.fibra_ro}`);
    if (csVal !== '' && formData.fibra_cs) parts.push(`${csVal}% ${formData.fibra_cs}`);
    // Solo incluir apresto en resultado si NO se va a reportar en tabla separada
    if (showApresto && pctA !== '' && reportarApresto === 'no_reportar') parts.push(`${pctA}% (apresto)`);

    setFormData(prev => ({ ...prev, resultadoQuimico: parts.join(', ') }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.m0, formData.m1, formData.d, formData.fibra_ro, formData.fibra_cs,
      formData.p0, formData.pf, formData.normaSeleccionada, showApresto, editMode]);

  const handleEditar = () => setEditMode(true);

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de Analisis Quimico? Esta acción no se puede deshacer.')) return;
    const processedData = processNormaData();
    const payload = {
      ...processedData,
      ROW_ID: rowId, // <-- siempre manda el rowId correcto
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      if (onSave) onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleBackFlush}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                ANÁLISIS QUÍMICO CUALITATIVO Y CUANTITATIVO
              </h1>
            </div>
          </div>
          {/* Botón Guardar eliminado para evitar duplicado */}
        </div>

        {/* Información del Pedido */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Información del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Pedido No: {pedidoId}</p>
          </CardContent>
        </Card>

        {/* Formulario Principal */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Formulario de Análisis</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Tabla principal */}
            <div className="overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  {/* Primera fila - NO. DE SOLICITUD y FECHA */}
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      NO. DE SOLICITUD:
                    </td>
                    <td className="border border-border p-1 w-1/6">
                      <Input
                        type="text"
                        placeholder="Número de solicitud"
                        value={formData.numeroSolicitud}
                        onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      FECHA:
                    </td>
                    <td className="border border-border p-1 w-1/6">
                      <Input
                        type="date"
                        placeholder="Fecha del análisis"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      REALIZÓ:
                    </td>
                    <td className="border border-border p-1 w-1/6">
                      <Input
                        type="text"
                        placeholder="Técnico que realizó"
                        value={formData.realizo}
                        onChange={(e) => handleInputChange('realizo', e.target.value)}
                        disabled={isReadOnly || mode === 'create'}
                        className="border-0 bg-transparent"
                      />
                    </td>
                  </tr>

                  {/* Segunda fila - Pretratamiento */}
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      PRETRATAMIENTO:
                    </td>
                    <td className="border border-border p-2" colSpan={5}>
                      <RadioGroup
                        value={formData.pretratamiento}
                        onValueChange={(value) => handleInputChange('pretratamiento', value)}
                        disabled={isReadOnly}
                        className="flex flex-row space-x-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="con_pretratamiento" id="con_pretratamiento" />
                          <Label htmlFor="con_pretratamiento">Con pretratamiento</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="sin_pretratamiento" id="sin_pretratamiento" />
                          <Label htmlFor="sin_pretratamiento">Sin pretratamiento</Label>
                        </div>
                      </RadioGroup>
                      {formData.pretratamiento === 'con_pretratamiento' && (
                        <div className="mt-2">
                          <Label className="block mb-1">Material no fibroso (Apresto):</Label>
                          <RadioGroup
                            value={reportarApresto}
                            onValueChange={setReportarApresto}
                            disabled={isReadOnly}
                            className="flex flex-row space-x-6"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="reportar" id="apresto_reportar" />
                              <Label htmlFor="apresto_reportar">Reportar</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no_reportar" id="apresto_no_reportar" />
                              <Label htmlFor="apresto_no_reportar">No reportar</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="solo_apresto" id="apresto_solo" />
                              <Label htmlFor="apresto_solo">Solo apresto</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Fila condicional - Sustancia (con pretratamiento) */}
                  {formData.pretratamiento === 'con_pretratamiento' && (
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        SUSTANCIA:
                      </td>
                      <td className="border border-border p-1" colSpan={5}>
                        <Textarea
                          placeholder="Sustancia utilizada en el pretratamiento..."
                          value={formData.sustancia}
                          onChange={(e) => handleInputChange('sustancia', e.target.value)}
                          disabled={isReadOnly}
                          className="min-h-[40px] border-0 bg-transparent resize-none"
                        />
                      </td>
                    </tr>
                  )}

                  {/* Tercera fila - Normas NMX */}
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      NORMA APLICABLE:
                    </td>
                    <td className="border border-border p-2" colSpan={3}>
                      <RadioGroup
                        value={formData.normaSeleccionada}
                        onValueChange={(value) => handleInputChange('normaSeleccionada', value)}
                        disabled={isReadOnly}
                        className="flex flex-row space-x-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="norma1833" id="norma1833" />
                          <Label htmlFor="norma1833">NMX-A-1833/1-INNTEX-2014</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="norma084" id="norma084" />
                          <Label htmlFor="norma084">NMX-A-084-INNTEX-2015</Label>
                        </div>
                      </RadioGroup>
                      {/* Subnorma solo si selecciona NMX-A-1833/1-INNTEX-2014 */}
                      {formData.normaSeleccionada === "norma1833" && (
                        <div className="mt-2">
                          <Label className="block mb-1">Subnorma NMX-A-1833:</Label>
                          <RadioGroup
                            value={subnorma1833}
                            onValueChange={setSubnorma1833}
                            disabled={isReadOnly}
                            className="flex flex-row space-x-6"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="NMX-A-1833-4" id="subnorma1833-4" />
                              <Label htmlFor="subnorma1833-4">NMX-A-1833-4</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="NMX-A-1833-11" id="subnorma1833-11" />
                              <Label htmlFor="subnorma1833-11">NMX-A-1833-11</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="NMX-A-1833-12" id="subnorma1833-12" />
                              <Label htmlFor="subnorma1833-12">NMX-A-1833-12</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium">
                      SUPERVISÓ:
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Supervisor del análisis"
                        value={formData.superviso}
                        onChange={(e) => handleInputChange('superviso', e.target.value)}
                        disabled={true}
                        className="border-0 bg-transparent"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabla de Apresto */}
            {showApresto && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border border-border text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border p-2 bg-muted font-semibold text-center" colSpan={3}>
                        MATERIAL NO FIBROSO (APRESTO)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium w-1/3">
                        P<sub>0</sub>
                      </td>
                      <td className="border border-border p-1 w-1/3">
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={formData.p0}
                          onChange={(e) => handleInputChange('p0', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                          step="0.1"
                        />
                      </td>
                      <td className="border border-border p-2 w-1/3" />
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        P<sub>f</sub>
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={formData.pf}
                          onChange={(e) => handleInputChange('pf', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                          step="0.1"
                        />
                      </td>
                      <td className="border border-border p-2" />
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        %A = ((P<sub>0</sub> - P<sub>f</sub>) / P<sub>0</sub>) × 100
                      </td>
                      <td className="border border-border p-2 text-center font-semibold">
                        {pctA !== '' ? `${pctA} %` : '—'}
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Fibra / componente"
                          value={formData.fibra}
                          onChange={(e) => handleInputChange('fibra', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Tabla de Contenido de Fibras - NMX-1833 */}
            {formData.normaSeleccionada === 'norma1833' && reportarApresto !== 'solo_apresto' && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border border-border text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border p-2 bg-muted font-semibold text-center" colSpan={3}>
                        CONTENIDO DE FIBRAS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium w-1/3">
                        m<sub>0</sub>
                      </td>
                      <td className="border border-border p-1 w-1/3">
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={formData.m0}
                          onChange={(e) => handleInputChange('m0', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                          step="0.1"
                        />
                      </td>
                      <td className="border border-border p-2 w-1/3" />
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        m<sub>1</sub>
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={formData.m1}
                          onChange={(e) => handleInputChange('m1', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                          step="0.1"
                        />
                      </td>
                      <td className="border border-border p-2" />
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        d
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="number"
                          placeholder="0.0"
                          value={formData.d}
                          onChange={(e) => handleInputChange('d', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                          step="0.1"
                        />
                      </td>
                      <td className="border border-border p-2" />
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        ρ = (100 × m<sub>1</sub> × d) / m<sub>0</sub>
                      </td>
                      <td className="border border-border p-2 text-center font-semibold">
                        {roVal !== '' ? `${roVal}` : '—'}
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Nombre de fibra"
                          value={formData.fibra_ro}
                          onChange={(e) => handleInputChange('fibra_ro', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        cs = 100 − ρ
                      </td>
                      <td className="border border-border p-2 text-center font-semibold">
                        {csVal !== '' ? `${csVal}` : '—'}
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Nombre de fibra"
                          value={formData.fibra_cs}
                          onChange={(e) => handleInputChange('fibra_cs', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Tabla de Contenido de Fibras - NMX-084 */}
            {formData.normaSeleccionada === 'norma084' && reportarApresto !== 'solo_apresto' && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border border-border text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border p-2 bg-muted font-semibold text-center" colSpan={2}>
                        CONTENIDO DE FIBRAS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium w-1/4">
                        COMBUSTIÓN
                      </td>
                      <td className="border border-border p-1">
                        <Textarea
                          placeholder="Resultado de combustión..."
                          value={formData.combustion}
                          onChange={(e) => handleInputChange('combustion', e.target.value)}
                          disabled={isReadOnly}
                          className="min-h-[60px] border-0 bg-transparent resize-none"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        MICROSCOPÍA
                      </td>
                      <td className="border border-border p-1">
                        <Textarea
                          placeholder="Resultado de microscopía..."
                          value={formData.microscopia}
                          onChange={(e) => handleInputChange('microscopia', e.target.value)}
                          disabled={isReadOnly}
                          className="min-h-[60px] border-0 bg-transparent resize-none"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border p-2 bg-muted font-medium">
                        ANÁLISIS QUÍMICO
                      </td>
                      <td className="border border-border p-1">
                        <Textarea
                          placeholder="Análisis químico..."
                          value={formData.analisis_quimico}
                          onChange={(e) => handleInputChange('analisis_quimico', e.target.value)}
                          disabled={isReadOnly}
                          className="min-h-[60px] border-0 bg-transparent resize-none"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Sección de Resultado */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      RESULTADO:
                    </td>
                    <td className="border border-border p-1">
                      <Textarea
                        placeholder="Resultado del análisis químico..."
                        value={formData.resultadoQuimico}
                        onChange={(e) => handleInputChange('resultadoQuimico', e.target.value)}
                        disabled={isReadOnly}
                        className="min-h-[150px] border-0 bg-transparent resize-none"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Observaciones */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-primary">Observaciones</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      OBSERVACIONES:
                    </td>
                    <td className="border border-border p-1 w-5/6">
                      <Textarea
                        placeholder="Observaciones del análisis..."
                        value={formData.observaciones}
                        onChange={(e) => handleInputChange('observaciones', e.target.value)}
                        disabled={isReadOnly}
                        className="min-h-[80px] border-0 bg-transparent resize-none"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

<div className="flex justify-end gap-4">
          <Button variant="outline" onClick={handleBackFlush}>
            Cancelar
          </Button>
          {/* Botones de acciones en modo view y no aceptado/rechazado SOLO PARA ADMIN */}
          {mode === 'view' && !editMode && puedeRevisar && !isAceptado && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={handleEditar}>Editar</Button>
            </>
          )}
          {/* Botón guardar edición SOLO PARA ADMIN */}
          {mode === 'view' && editMode && puedeRevisar && !isAceptado && (
            <Button variant="default" onClick={handleGuardarEdicion}>
              <Save className="h-4 w-4 mr-2" />
              Guardar Edición
            </Button>
          )}
          {/* Botón guardar en modo create */}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>
              <Save className="h-4 w-4 mr-2" />
              {guardando ? 'Guardando...' : 'Guardar Formulario'}
            </Button>
          )}
        </div>
      </div>
      {/* Modal para razón de rechazo */}
      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}